import '../config/teams.json'
import { useSocketEvent, useSocketState } from '../helpers/useSocket'
import { Bar1v1 } from '../components/Bar100'
import { BarIndividual } from '../components/BarIndividual'
import { LandmarkDisplay } from '../components/LandmarkDisplay'
import { UnitDisplay } from '../components/UnitDisplay'
import { PlayerStats } from '../components/PlayerStats'
import colors from '../config/colors.json'
import { TeamIcon, StripTeamTag } from '../components/TeamIcon'
import ChartjsPluginAnnotation from 'chartjs-plugin-annotation'
import React, { useState, useEffect } from 'react'
import { socket } from '../socket'

import { convertSummary } from '../helpers/convertSummary'
import { usePbgidIcons } from '../helpers/pbgidIcons'
import { fancyTimeFormat } from '../helpers/fancyTimeFormat'
import { matchTypeLabel } from '../helpers/matchTypeLabel'
import { findMap } from '../helpers/drafting'

import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js'

import { Stats } from '../components/Stats'
import { MilitaryChart } from '../components/MilitaryChart'
import { EconomyChart } from '../components/EconomyChart'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ChartjsPluginAnnotation)

// Rows of the individual resource grid. Optional rows are hidden when neither player gathered any.
const RESOURCE_ROWS = [
	{ name: 'TOTAL', key: 'total', icon: '/liveGameIcons/improved_deposits.png' },
	{ name: 'FOOD', key: 'food', icon: '/liveGameIcons/food.png' },
	{ name: 'WOOD', key: 'wood', icon: '/liveGameIcons/wood.png' },
	{ name: 'GOLD', key: 'gold', icon: '/liveGameIcons/gold.png' },
	{ name: 'STONE', key: 'stone', icon: '/liveGameIcons/stone.png' },
]

export default function Display() {
	const [data, setData] = useState()
	const pbgidIcons = usePbgidIcons()
	const [militaryLead, setMilitaryLead] = useState()
	const [summary, setSummary] = useState({})
	const [config, setConfig] = useState({})

	const [annotations, setAnnotations] = useState([])
	const [economyAnnotations, setEconomyAnnotations] = useState([])

	const [isReady, setIsReady] = useState(false)
	const [player1win, setPlayer1win] = useState(true)

	const [extraInfo, setExtraInfo] = useState({})
	const [match] = useSocketState('Match')
	const [tournament] = useSocketState('Tournament')

	const [themeConfig, setThemeConfig] = useState(null)

	const [animationKey, setAnimationKey] = useState(0)

	useSocketEvent('updateCurrent', (newdata) => {
		setData(newdata.summary)
		setExtraInfo(newdata.extra)
	})

	useSocketEvent('postgameAnimate', () => {
		setAnimationKey((key) => key + 1)
	})

	useEffect(() => {
		socket.emit('registerPostgame')
	}, [])
	useSocketEvent('updateConfig', (newdata) => {
		console.log(`Received updateConfig`, newdata)
		setConfig(newdata)
	})

	useEffect(() => {
		if (!data || !data.players || !match) {
			setIsReady(false)
			return
		}

		let _summary = convertSummary(data, pbgidIcons)

		let _annotations = []
		let _economyAnnotations = []
		let _militaryAnnotations = []

		// Swap players if extrainfo wants it
		if (_summary && _summary?.players.length === 2) {
			const p0name = _summary.players[0].name
			const p1name = _summary.players[1].name
			const tournamentPlayers = tournament?.config?.players
			const p0stripped = StripTeamTag(p0name, tournamentPlayers).trim()
			const p1stripped = StripTeamTag(p1name, tournamentPlayers).trim()
			if (
				p1name === match?.leftPlayer.name ||
				p0name === match?.rightPlayer.name ||
				p1stripped === match?.leftPlayer.name ||
				p0stripped === match?.rightPlayer.name ||
				match?.leftPlayer.altNames?.includes(p1name) ||
				match?.rightPlayer.altNames?.includes(p0name) ||
				match?.leftPlayer.altNames?.includes(p1stripped) ||
				match?.rightPlayer.altNames?.includes(p0stripped)
			) {
				_summary.players = [_summary.players[1], _summary.players[0]]
			}
		}

		_summary?.players.forEach((player, index) => {
			const matchPlayer = index ? match.rightPlayer : match.leftPlayer
			const playerColor = colors[matchPlayer.color]

			if (player.feudalAge) {
				_annotations.push({
					type: 'line',
					scaleID: 'x',
					value: player.feudalAge,
					borderColor: playerColor?.mainColor,
					borderWidth: 4,
					borderDash: [4, 4],
					label: {
						display: true,
						backgroundColor: 'rgba(0,0,0,0.1)',
						color: playerColor?.mainColor,
						position: index ? '5%' : '25%',
						font: { size: 20 },
						padding: { left: 4, top: 4, right: 4, bottom: 2 },
						content: age2img,
					},
				})
			}
			if (player.castleAge) {
				_annotations.push({
					type: 'line',
					scaleID: 'x',
					value: player.castleAge,
					borderColor: playerColor?.mainColor,
					borderWidth: 4,
					borderDash: [4, 4],
					label: {
						display: true,
						backgroundColor: 'rgba(0,0,0,0.1)',
						color: playerColor?.mainColor,
						position: index ? '5%' : '25%',
						font: { size: 20 },
						padding: { left: 4, top: 4, right: 4, bottom: 2 },
						content: age3img,
					},
				})
			}
			if (player.imperialAge) {
				_annotations.push({
					type: 'line',
					scaleID: 'x',
					value: player.imperialAge,
					borderColor: playerColor?.mainColor,
					borderWidth: 4,
					borderDash: [4, 4],
					label: {
						display: true,
						backgroundColor: 'rgba(0,0,0,0.1)',
						color: playerColor?.mainColor,
						position: index ? '5%' : '25%',
						font: { size: 20 },
						padding: { left: 4, top: 4, right: 4, bottom: 2 },
						content: age4img,
					},
				})
			}
			player.townCenters?.forEach((time) => {
				_economyAnnotations.push({
					type: 'line',
					scaleID: 'x',
					value: time,
					borderColor: playerColor?.mainColor,
					borderWidth: 2,
					borderDash: [4, 4],
					label: {
						display: true,
						backgroundColor: 'rgba(0,0,0,0.1)',
						color: playerColor?.mainColor,
						position: index ? '5%' : '25%',
						font: { size: 20 },
						padding: { left: 4, top: 4, right: 4, bottom: 2 },
						content: townCenterImg,
					},
				})
			})
			player.keeps?.forEach((time) => {
				_militaryAnnotations.push({
					type: 'line',
					scaleID: 'x',
					value: time,
					borderColor: playerColor?.mainColor,
					borderWidth: 2,
					borderDash: [4, 4],
					label: {
						display: true,
						backgroundColor: 'rgba(0,0,0,0.1)',
						color: playerColor?.mainColor,
						position: index ? '5%' : '25%',
						font: { size: 20 },
						padding: { left: 4, top: 4, right: 4, bottom: 2 },
						content: keepImg,
					},
				})
			})
		})

		setMilitaryLead(
			_summary?.players[0].timeline.map((v, i) => ({
				x: v.time,
				y: v.score_military - _summary?.players[1].timeline[i].score_military,
			})),
		)

		if (_summary.players[1].result === 'win') {
			setPlayer1win(false)
		} else {
			setPlayer1win(true)
		}

		setSummary(_summary)
		setAnnotations([..._annotations, ..._militaryAnnotations])
		setEconomyAnnotations([..._annotations, ..._economyAnnotations])

		setIsReady(true)
	}, [data, pbgidIcons, !!match, match?.leftPlayer.name, match?.leftPlayer?.color, match?.rightPlayer?.color, tournament?.config?.players])

	function _onUpdateConfig(value) {
		console.log(`Received updateConfig`, value)
		setConfig((oldState) => ({ ...oldState, ...value }))
	}

	const age2img = new Image(30, 30)
	age2img.src = '/age_icons/age_2_dark.png'
	const age3img = new Image(30, 30)
	age3img.src = '/age_icons/age_3_dark.png'
	const age4img = new Image(30, 30)
	age4img.src = '/age_icons/age_4_dark.png'
	const townCenterImg = new Image(35, 35)
	townCenterImg.src = '/liveGameIcons/town_center.png'
	const keepImg = new Image(35, 35)
	keepImg.src = '/liveGameIcons/keep.png'

	document.body.style.overflow = 'hidden'

	useEffect(() => {
		const themeName = config.theme ?? config.cssTheme?.replace('.css', '.json')
		if (!themeName) return
		fetch(`/themes/${themeName}`)
			.then((r) => r.json())
			.then(setThemeConfig)
			.catch(() => setThemeConfig(null))
	}, [config.theme, config.cssTheme])

	const cssFiles = themeConfig?.css ?? (config.cssTheme ? [config.cssTheme] : [])
	const cssVars = Object.fromEntries(Object.entries(themeConfig?.cssVariables ?? {}).map(([k, v]) => [k.startsWith('--') ? k : `--${k}`, v]))

	const theme = themeConfig?.postGame ?? {}
	const resourceDisplayType = theme.resourceDisplayType ?? 'stacked-bars'
	// 'table' is the classic stats table, 'stacked-bars' renders each stat as a Bar1v1 like the resources
	const statsDisplayType = theme.statsDisplayType ?? 'table'
	const showMapImage = theme.showMapImage ?? false
	const showStrategy = theme.showStrategy ?? false
	const showLandmarks = theme.showLandmarks ?? false
	const showUnitComp = theme.showUnitComp ?? false
	// Per player stat list on the sides, the same numbers the Stats table shows
	const showPlayerStats = theme.showPlayerStats ?? false
	const showStats = theme.showStats ?? true
	const showCountry = theme.showCountry ?? true
	const showPlayerPicture = theme.showPlayerPicture ?? false
	const showVikingBonusFood = theme.showVikingBonusFood ?? false
	// Vikings free vills into account
	const vikingBonusFood = (i) => {
		const player = summary?.players?.[i]
		if (!showVikingBonusFood || player?.civilization !== 'vikings') return undefined
		return player.totalResourcesGathered.food + Math.max(0, (player.villagersMade ?? 0) - 6) * 50
	}

	const leftPlayerInfo = tournament?.config?.players?.find((p) => match?.leftPlayer?.name?.toLowerCase().includes(p.name.toLowerCase()))
	const rightPlayerInfo = tournament?.config?.players?.find((p) => match?.rightPlayer?.name?.toLowerCase().includes(p.name.toLowerCase()))

	// Players without their own draft picture fall back to the tournament placeholder.
	const placeholderPicture = tournament?.config?.settings?.placeholderPlayerPictureUrl
	const leftPlayerPicture = leftPlayerInfo?.draftPicture || placeholderPicture
	const rightPlayerPicture = rightPlayerInfo?.draftPicture || placeholderPicture

	// Same rows as the Stats table. Optional rows are hidden when both players are at zero.
	const STAT_ROWS = [
		{ name: 'RELICS', optional: true, get: (i) => (i ? extraInfo.rightRelics : extraInfo.leftRelics) ?? 0 },
		{ name: 'MAX VILLS', get: (i) => summary?.players?.[i]?.maxVillagers ?? 0 },
		{ name: 'MAX TRADE', optional: true, get: (i) => summary?.players?.[i]?.maxTraders ?? 0 },
		{ name: 'MAX FISH', optional: true, get: (i) => summary?.players?.[i]?.maxFishingShips ?? 0 },
		{ name: 'APM', get: (i) => summary?.players?.[i]?.apm ?? 0 },
		// Eco killed by a player is the economy the other one lost
		{ name: 'ECO KILLED', get: (i) => summary?.players?.[i ? 0 : 1]?.economyLost ?? 0 },
	]

	return (
		<div
			className={`postgame${animationKey ? ' postgame--animate' : ''}`}
			data-resource-display={resourceDisplayType}
			data-stats-display={statsDisplayType}
			data-show-map-image={showMapImage}
			data-show-strategy={showStrategy}
			data-show-landmarks={showLandmarks}
			data-show-unit-comp={showUnitComp}
			data-show-player-stats={showPlayerStats}
			data-show-stats={showStats}
			data-show-country={showCountry}
			data-show-player-picture={showPlayerPicture}
			style={{
				'--left-player-color': colors[match?.leftPlayer?.color]?.mainColor,
				'--left-player-color-secondary': colors[match?.leftPlayer?.color]?.secondaryColor,
				'--left-player-color-alpha': colors[match?.leftPlayer?.color]?.mainColorAlpha,
				'--left-player-color-alpha7': colors[match?.leftPlayer?.color]?.mainColorAlpha7,
				'--right-player-color': colors[match?.rightPlayer?.color]?.mainColor,
				'--right-player-color-secondary': colors[match?.rightPlayer?.color]?.secondaryColor,
				'--right-player-color-alpha': colors[match?.rightPlayer?.color]?.mainColorAlpha,
				'--right-player-color-alpha7': colors[match?.rightPlayer?.color]?.mainColorAlpha7,
				...cssVars,
			}}>
			{cssFiles.map((css) => (
				<link key={css} rel="stylesheet" type="text/css" href={`/themes/${css}`} />
			))}

			{isReady && !!data && !!match ? (
				<div className="flags">
					<div className="flags__left">
						<img className={`flags__left__img`} src={`/civFlags/${summary?.players[0].civilization}.png`} alt="Civ" />
					</div>
					<div className="flags__right">
						<img className={`flags__right__img`} src={`/civFlags/${summary?.players[1].civilization}.png`} alt="Civ" />
					</div>

					<div className="header">
						<div className="header__leftPlayer header__player">
							{showPlayerPicture && !!leftPlayerPicture && <img className="header__leftPlayer__picture header__playerPicture" src={leftPlayerPicture} alt="" />}
							{showCountry && !!match.leftPlayer.country && (
								<div className="header__leftCountry header__country">
									<img src={`https://flagpedia.net/data/flags/emoji/google/160x160/${match.leftPlayer.country}.png`} alt="" />
								</div>
							)}
							<div className="header__leftPlayer__name_civ">
								<div className={`header__leftPlayer__name header__playerName ${player1win ? 'header__winner' : 'header__loser'}`}>
									{StripTeamTag(summary?.players[0].name, tournament?.config?.players).replace('Beell', 'BEE')}
								</div>
								<div className={`header__leftPlayer__civ header__playerCiv ${player1win ? 'header__winner' : 'header__loser'}`}>
									{summary?.players[0].civilization.replace(/_/g, ' ').toUpperCase()}
								</div>
							</div>
							<TeamIcon
								playerName={summary?.players[0].name}
								players={tournament?.config?.players}
								className={`header__leftPlayer__team ${player1win ? 'header__winner' : 'header__loser'}`}
							/>
						</div>

						<div className={`header__leftResult header__result ${player1win ? 'header__winner' : 'header__loser'}`}>{summary?.players[0].result.toUpperCase()}</div>

						<div className={`header__leftScore header__score ${player1win ? 'header__winner' : 'header__loser'}`}>{match.leftPlayer.score}</div>

						<div className="header__duration_mapName">
							<div className="header__duration">{fancyTimeFormat(summary?.duration)}</div>
							<div className="header__mapName">{summary?.mapName.toUpperCase().replace('EGC - ', '').replace('EGCMS - ', '').replace('KOTR - ', '')}</div>
							<div className="header__matchType">{matchTypeLabel(match, 'long')}</div>
							{showMapImage && (
								<div className="header__map">
									<img className="header__mapImage" src={findMap(summary?.mapName, { withFallback: true }).iconUrl} alt="" />
								</div>
							)}
							<div className="header__dash" />
						</div>

						<div className={`header__rightScore header__score ${player1win ? 'header__loser' : 'header__winner'}`}>{match.rightPlayer.score}</div>

						<div className={`header__rightResult header__result ${player1win ? 'header__loser' : 'header__winner'}`}>{summary?.players[1].result.toUpperCase()}</div>

						<div className="header__rightPlayer header__player">
							<TeamIcon
								playerName={summary?.players[1].name}
								players={tournament?.config?.players}
								className={`${player1win ? 'header__loser' : 'header__winner'} header__rightPlayer__team`}
							/>
							<div className="header__rightPlayer__name_civ ">
								<div className={`header__rightPlayer__name header__playerName ${player1win ? 'header__loser' : 'header__winner'}`}>
									{StripTeamTag(summary?.players[1].name, tournament?.config?.players).replace('Beell', 'BEE')}
								</div>
								<div className={`header__rightPlayer__civ header__playerCiv ${player1win ? 'header__loser' : 'header__winner'}`}>
									{summary?.players[1].civilization.replace(/_/g, ' ').toUpperCase()}
								</div>
							</div>
							{showCountry && !!match.rightPlayer.country && (
								<div className="header__rightCountry header__country">
									<img src={`https://flagpedia.net/data/flags/emoji/google/160x160/${match.rightPlayer.country}.png`} alt="" />
								</div>
							)}
							{showPlayerPicture && !!rightPlayerPicture && <img className="header__rightPlayer__picture header__playerPicture" src={rightPlayerPicture} alt="" />}
						</div>
					</div>

					<hr className="divider" />

					<div className="resources">
						<div className="resources__container">
							{resourceDisplayType === 'stacked-bars' && (
								<div className="resources__container__bars">
									<Bar1v1
										name="TOTAL RESOURCES GATHERED"
										player1={summary?.players[0].totalResourcesGathered.total}
										player2={summary?.players[1].totalResourcesGathered.total}
										leftColor={colors[match.leftPlayer.color]?.mainColor}
										rightColor={colors[match.rightPlayer.color]?.mainColor}
									/>
									<Bar1v1
										name="FOOD"
										player1={summary?.players[0].totalResourcesGathered.food}
										player2={summary?.players[1].totalResourcesGathered.food}
										leftColor={colors[match.leftPlayer.color]?.mainColor}
										rightColor={colors[match.rightPlayer.color]?.mainColor}
									/>
									<Bar1v1
										name="WOOD"
										player1={summary?.players[0].totalResourcesGathered.wood}
										player2={summary?.players[1].totalResourcesGathered.wood}
										leftColor={colors[match.leftPlayer.color]?.mainColor}
										rightColor={colors[match.rightPlayer.color]?.mainColor}
									/>
									<Bar1v1
										name="GOLD"
										player1={summary?.players[0].totalResourcesGathered.gold}
										player2={summary?.players[1].totalResourcesGathered.gold}
										leftColor={colors[match.leftPlayer.color]?.mainColor}
										rightColor={colors[match.rightPlayer.color]?.mainColor}
									/>
									{(summary?.players[0].totalResourcesGathered.stone !== 0 || summary?.players[1].totalResourcesGathered.stone !== 0) && (
										<Bar1v1
											name="STONE"
											player1={summary?.players[0].totalResourcesGathered.stone}
											player2={summary?.players[1].totalResourcesGathered.stone}
											leftColor={colors[match.leftPlayer.color]?.mainColor}
											rightColor={colors[match.rightPlayer.color]?.mainColor}
										/>
									)}
									{(summary?.players[0].totalResourcesGathered.oliveoil !== 0 || summary?.players[1].totalResourcesGathered.oliveoil !== 0) && (
										<Bar1v1
											name={(() => {
												const label = (civ) => (civ === 'byzantines' ? 'OLIVE OIL' : civ === 'macedonian_dynasty' ? 'SILVER' : null)
												const left = label(summary?.players[0].civilization)
												const right = label(summary?.players[1].civilization)
												if (left && right && left !== right) return `${left} / ${right}`
												return left || right || 'OLIVE OIL'
											})()}
											player1={summary?.players[0].totalResourcesGathered.oliveoil}
											player2={summary?.players[1].totalResourcesGathered.oliveoil}
											leftColor={colors[match.leftPlayer.color]?.mainColor}
											rightColor={colors[match.rightPlayer.color]?.mainColor}
										/>
									)}
								</div>
							)}

							{resourceDisplayType === 'individual' && (
								<div className="resources__container__individual" key={`resources-${animationKey}`}>
									<div className="barIndividual__head barIndividual__head--label" />
									<div className="barIndividual__head barIndividual__head--left">{StripTeamTag(summary?.players[0].name, tournament?.config?.players)}</div>
									<div className="barIndividual__head barIndividual__head--right">{StripTeamTag(summary?.players[1].name, tournament?.config?.players)}</div>
									{RESOURCE_ROWS.filter(
										({ key, optional }) =>
											!optional || summary?.players[0].totalResourcesGathered[key] !== 0 || summary?.players[1].totalResourcesGathered[key] !== 0,
									).map(({ name, key, icon }) => (
										<BarIndividual
											key={key}
											name={name}
											icon={icon}
											player1={summary?.players[0].totalResourcesGathered[key]}
											player2={summary?.players[1].totalResourcesGathered[key]}
											player1Bonus={key === 'food' ? vikingBonusFood(0) : undefined}
											player2Bonus={key === 'food' ? vikingBonusFood(1) : undefined}
											leftColor={colors[match.leftPlayer.color]?.mainColor}
											rightColor={colors[match.rightPlayer.color]?.mainColor}
										/>
									))}
								</div>
							)}

							{showStats && statsDisplayType === 'table' && (
								<div className="stats">
									<Stats summary={summary} extraInfo={extraInfo} />
								</div>
							)}

							{showStats && statsDisplayType === 'stacked-bars' && (
								<div className="resources__container__bars stats__bars">
									{STAT_ROWS.filter(({ optional, get }) => !optional || get(0) !== 0 || get(1) !== 0).map(({ name, get }) => (
										<Bar1v1
											key={name}
											name={name}
											player1={get(0)}
											player2={get(1)}
											leftColor={colors[match.leftPlayer.color]?.mainColor}
											rightColor={colors[match.rightPlayer.color]?.mainColor}
										/>
									))}
								</div>
							)}
						</div>

						<div className="charts">
							<MilitaryChart
								key={`military-${animationKey}`}
								match={match}
								militaryLead={militaryLead}
								annotations={annotations}
								height="355px"
								animate={animationKey > 0}
							/>
							<EconomyChart
								key={`economy-${animationKey}`}
								match={match}
								summary={summary}
								annotations={economyAnnotations}
								height="355px"
								animate={animationKey > 0}
							/>
						</div>
					</div>
					{showLandmarks && (
						<div className="landmarksContainer" key={`landmarks-${animationKey}`}>
							<LandmarkDisplay landmarks={summary?.players[0].analysis?.landmarks} side="left" />
							<LandmarkDisplay landmarks={summary?.players[1].analysis?.landmarks} side="right" />
						</div>
					)}

					{showStrategy && (
						<div className="strategyContainer" key={`strategy-${animationKey}`}>
							{[0, 1].map((index) => {
								const strategy = summary?.players[index].strategy
								if (!strategy) return <div key={index} className={`strategy strategy--${index ? 'right' : 'left'}`} />

								return (
									<div key={index} className={`strategy strategy--${index ? 'right' : 'left'}`}>
										<div className="strategy__divider">
											<span className="strategy__dividerLabel">Strategy</span>
										</div>
										<div className="strategy__text">
											{/* A matching prefix rule is rendered in front of the strategy, separated by its own divider */}
											<div className="strategy__name">
												{strategy.prefix && (
													<>
														<span className="strategy__prefix">{strategy.prefix.name}</span>
														<span className="strategy__prefixDivider">{strategy.prefix.divider}</span>
													</>
												)}
												<span className="strategy__mainName">{strategy.name}</span>
											</div>
										</div>
									</div>
								)
							})}
						</div>
					)}

					{showUnitComp && (
						<div className="unitCompContainer" key={`unitComp-${animationKey}`}>
							{[0, 1].map((index) => {
								const side = index ? 'right' : 'left'
								// One offs are noise, they are usually a scout or a single unit left over from an aborted plan
								const units = (summary?.players[index].unitComp ?? []).filter((unit) => unit.count > 2).slice(0, 5)

								return (
									<div key={index} className={`unitComp unitComp--${side}`}>
										<div className="unitComp__divider">
											<span className="unitComp__dividerLabel">Unit Composition</span>
										</div>
										<div className="unitComp__units">
											{units.map((unit) => (
												<UnitDisplay key={unit.key} unit={unit} side={side} />
											))}
										</div>
									</div>
								)
							})}
						</div>
					)}

					{showPlayerStats && (
						<div className="playerStatsContainer" key={`playerStats-${animationKey}`}>
							{[0, 1].map((index) => (
								<PlayerStats key={index} summary={summary} extraInfo={extraInfo} index={index} side={index ? 'right' : 'left'} />
							))}
						</div>
					)}

					<div className="dataProvider">Data provided by aoe4world.com</div>

					{/* DEV: pinned strategy readout, analyzed server side in server/helpers/strategy
					<div
						className="strategyDebug"
						style={{
							position: 'fixed',
							bottom: 0,
							left: 0,
							right: 0,
							zIndex: 9999,
							display: 'flex',
							justifyContent: 'space-between',
							gap: '20px',
							padding: '8px 16px',
							background: 'rgba(0,0,0,0.8)',
							color: '#fff',
							font: '16px monospace',
							pointerEvents: 'none',
						}}>
						{[0, 1].map((i) => {
							const player = summary?.players?.[i]
							const strategy = player?.strategy
							return (
								<div key={i} style={{ textAlign: i ? 'right' : 'left' }}>
									<div style={{ fontSize: '20px' }}>
										{StripTeamTag(player?.name, tournament?.config?.players)}
										{': '}
										<b>{strategy?.name ?? 'NO STRATEGY'}</b>
									</div>
									<div style={{ opacity: 0.6 }}>{strategy?.matched?.join(' · ')}</div>
									<div style={{ opacity: 0.85 }}>{player?.ageUps?.map((ageUp) => `${ageUp.name} ${fancyTimeFormat(ageUp.gameTime)}`).join(' · ')}</div>
								</div>
							)
						})}
					</div> */}
				</div>
			) : (
				<></>
			)}
		</div>
	)
}
